import dayjs from "dayjs";
import { object, string } from "yup";

import { ICustomerSalesProductPayment } from "@/interfaces/customer-sales-product-payment.interfaces";
import ICustomerSalesProduct from "@/interfaces/customer-sales-product.interfaces";
import ICustomer from "@/interfaces/customer.interfaces";
import { IRenewTotal } from "@/interfaces/renew.interfaces";
import ISalesProductPrice from "@/interfaces/sales-product-price.interfaces";
import ISalesProduct from "@/interfaces/sales-product.interfaces";
import { ISubscribeInput } from "@/interfaces/subscribe.interfaces";
import { RenewMessages } from "@/messages/renew/renew.messages";
import { CustomerSalesProductStatus } from "@/types/customer-sales-product.types";
import { ToastProps } from "@/types/toast.types";

import { SubscribeFormData } from "./types";

export const buildFormInitialData = (
  customer: ICustomer | null,
  customerSalesProductPayment: ICustomerSalesProductPayment | null,
  totalPayment: IRenewTotal | null
): SubscribeFormData => {
  return {
    name: customer?.name || "",
    document: customer?.federalTaxId || "",
    zipCode: customer?.zipCode || "",
    address: customer?.address || "",
    number: customer?.addressNumber || "",
    complement: customer?.addressComp || "",
    neighborhood: customer?.district || "",
    city: customer?.city || "",
    state: customer?.state || "",
    customerSalesProductId: customerSalesProductPayment?.customerSalesProductId || "",
    totalPaid: totalPayment?.totalCustomerSalesProduct || 0,
    totalEventValue: totalPayment?.totalEventValue || 0,
    totalCustomerSalesProduct: totalPayment?.totalCustomerSalesProduct || 0,
  };
};

export const buildFormSchema = (messages: RenewMessages) => {
  return object().shape({
    name: string().required(messages.errorRequiredName),
    document: string().required(messages.errorRequiredCnpjCpf),
    zipCode: string().required(messages.errorRequiredCep),
    address: string().required(messages.errorRequiredAddress),
    number: string().required(messages.errorRequiredNumber),
    neighborhood: string().required(messages.errorRequiredNeighborhood),
    city: string().required(messages.errorRequiredCity),
    state: string().required(messages.errorRequiredState),
  });
};

export const normalizeData = (
  formData: SubscribeFormData,
  customer: ICustomer,
  salesProduct: ISalesProduct,
  salesProductPrice: ISalesProductPrice
): ISubscribeInput => {
  return {
    salesProductId: salesProduct.id,
    startDate: new Date().toISOString(),
    endDate: dayjs().add(1, "month").toDate().toISOString(),
    salesProductPriceId: salesProductPrice.id,
    planName: salesProductPrice.planName,
    pricePerEvent: salesProductPrice.pricePerEvent,
    eventsIncludedMonthlyFee: salesProductPrice.eventsIncludedMonthlyFee,
    feePerSalesConvertion: salesProductPrice.feePerSalesConvertion,
    fixedFeePerSalesConvertion: salesProductPrice.fixedFeePerSalesConvertion,
    fixedMonthlyFee: salesProductPrice.fixedMonthlyFee,
    nextPriceUpdate: dayjs().add(1, "month").toDate().toISOString(),
    dueDay: 1,
    customerSalesProductId: formData.customerSalesProductId,
    totalCustomerSalesProduct: formData.totalCustomerSalesProduct,
    totalEventValue: formData.totalEventValue,
    totalPaid: formData.totalPaid,
    customer: {
      ...customer,
      name: formData.name,
      federalTaxId: formData.document,
      address: formData.address,
      addressNumber: formData.number,
      addressComp: formData.complement || "",
      district: formData.neighborhood,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
    },
  };
};

export const hasExistingPlan = (
  salesProductPrice: ISalesProductPrice,
  showToast: (toast: ToastProps) => void,
  existingPlanError: string,
  customerSalesProducts?: ICustomerSalesProduct[]
): void => {
  if (!customerSalesProducts) return;

  const hasExistingPlan = customerSalesProducts.some(
    (product) =>
      product.salesProduct.id === salesProductPrice.salesProductId &&
      product.status === CustomerSalesProductStatus.Active
  );

  if (hasExistingPlan) return showToast({ text: existingPlanError, type: "error" });
};
